import React, { Dispatch, Fragment, SetStateAction, useState } from 'react';
import { FragmentData, gql } from '@ts-gql/tag';

import { DataTable, sortTableData } from '@reckon-web/data-table';
import { Box } from '@reckon-web/box';
import { Inline } from '@reckon-web/inline';
import { Stack } from '@reckon-web/stack';
import { UserAvatar } from '@reckon-web/avatar';
import { Text } from '@reckon-web/text';
import { TextLinkButton } from '@reckon-web/text-link';
import { Heading } from '@reckon-web/heading';
import { Tag } from '@reckon-web/tag';

import { getKeyFromText } from '../../utilities/colorKeys';

import { Partner } from './tableComponents/Partner';
import { Manager } from './tableComponents/Manager';
import { Active } from './tableComponents/Active';
import { Product, Role } from './types';

type TableDataRow = { [key: string]: any };

// gql
export let usersFragment = gql`
  fragment TenantUsers_user on TenantUser {
    active
    apsUserId
    cloudId
    createdOn
    email
    fullName
    manager
    mfaRequired
    partner
    products {
      productId
      friendlyName
      internalName
    }
    roleName
    tenantId
  }
` as import('../../../__generated__/ts-gql/TenantUsers_user').type;

function getToneFromText(text: string) {
  const tones = [0, 1, 2, 3, 4, 5] as const;
  return getKeyFromText(tones, text);
}

type UsersTableProp = {
  tenantUsers: FragmentData<typeof usersFragment>[];
  setDrawerOpen: Dispatch<SetStateAction<boolean>>;
  setEditUser: Dispatch<
    SetStateAction<FragmentData<typeof usersFragment> | null>
  >;
  rootCanDeactivate: boolean;
  roleList: Role[];
  searchVal?: string;
  isFiltersApplied?: boolean;
  productList: readonly Product[];
  canEditUser?: boolean;
};

// components
export function UsersTable({
  tenantUsers,
  setDrawerOpen,
  setEditUser,
  rootCanDeactivate,
  roleList,
  isFiltersApplied,
  searchVal,
  productList,
  canEditUser,
}: UsersTableProp) {
  const [sort, setSort] = useState({ key: 'customFullName', asc: true });

  const sortedData: TableDataRow | FragmentData<typeof usersFragment>[] =
    sortTableData(
      tenantUsers.map((i) => {
        if (i.apsUserId === 0) {
          return {
            ...i,
            roleName: 'SYSTEM_USER',
            roleDescription: 'SYSTEM_USER',
            customFullName: i.fullName?.toUpperCase()?.trim(),
            products: [],
          };
        }
        return {
          ...i,
          customFullName: i.fullName?.toUpperCase()?.trim(),
          roleDescription: roleList.find((a) => a.roleName === i.roleName)
            ?.roleDescription,
        };
      }),
      sort
    );

  const EmptyState = ({
    heading,
    content,
  }: {
    heading: string;
    content: string;
  }) => {
    return (
      <Stack gap="medium" align="center">
        <Heading level="3">{heading}</Heading>
        <Text color="muted" size="small">
          {content}
        </Text>
      </Stack>
    );
  };

  const productsAndTagColours = productList.map((product, index) => ({
    productName: product.friendlyName,
    colourNo: index % 5,
  }));

  return (
    <DataTable
      sort={sort}
      allowOverflow
      onSortChange={(s) => {
        if (
          s?.key === 'fullName' ||
          s?.key === 'roleName' ||
          s?.key === sort.key
        )
          return setSort(s);

        // @ts-ignore makes it descend on initial click for these cols rather than ascend
        setSort({ key: s?.key, asc: !s?.asc });
      }}
      rowCount={sortedData.length}
      columns={{
        customFullName: {
          label: 'User',
          render: (index) => (
            <Inline alignY="center" gap="small">
              <UserAvatar
                name={sortedData[index].fullName || ''}
                size="small"
              />
              <Stack>
                <Box paddingY="xsmall">
                  {sortedData[index].apsUserId === 0 ? (
                    <Text size="xsmall" color="dim" weight="semibold">
                      {sortedData[index].fullName || ''}
                    </Text>
                  ) : (
                    <Text size="xsmall">
                      <TextLinkButton
                        onClick={() => {
                          setDrawerOpen((prev) => !prev);
                          setEditUser(sortedData[index]);
                        }}
                      >
                        {sortedData[index].fullName || ''}
                      </TextLinkButton>
                    </Text>
                  )}
                </Box>
                <Text size="xsmall">{sortedData[index].email}</Text>
              </Stack>
            </Inline>
          ),
        },
        invited: {
          label: '',
          render: (index) => <div></div>,
        },
        manager: {
          label: 'Manager',
          render: (index) => (
            <Manager canEditUser={canEditUser} tenantUser={sortedData[index]} />
          ),
        },
        partner: {
          label: 'Partner',
          render: (index) => (
            <Partner tenantUser={sortedData[index]} canEditUser={canEditUser} />
          ),
        },
        roleDescription: {
          label: 'Policy',
          render: (index) => (
            <Text size="small">
              {sortedData[index].roleDescription?.toUpperCase()}
            </Text>
          ),
        },
        products: {
          label: 'Products',
          render: (index) => {
            const { products } = sortedData[index];

            return (
              <Inline gap="xsmall">
                {products?.map(({ friendlyName, productId }: Product) => {
                  const getProduct = productsAndTagColours.find(
                    (product) => product.productName === friendlyName
                  );
                  const colourNo =
                    getProduct?.colourNo ?? getToneFromText(friendlyName);

                  return (
                    <Tag
                      key={productId}
                      label={friendlyName}
                      variant={colourNo}
                    />
                  );
                })}
              </Inline>
            );
          },
        },
        active: {
          label: 'Active',
          render: (index) => {
            return sortedData[index].apsUserId === 0 ? (
              <Fragment />
            ) : (
              <Active
                canEditUser={canEditUser}
                tenantUser={sortedData[index]}
                rootCanDeactivate={rootCanDeactivate}
                currentRole={sortedData[index].roleName || 'user'}
              />
            );
          },
        },
      }}
      emptyState={
        (!tenantUsers.length && (
          <>
            {!searchVal && !isFiltersApplied && (
              <EmptyState
                heading="You have no users yet"
                content="Users will show up here as they are added."
              />
            )}
            {!searchVal && isFiltersApplied && (
              <EmptyState
                heading="No users found"
                content="Try adjusting filters or search their name. Note that searching users is case sensitive."
              />
            )}
            {!!searchVal && (
              <EmptyState
                heading={`No users found for "${searchVal}".`}
                content="Check that all words are spelt correctly or try adjusting filters. Note that searching users is case sensitive."
              />
            )}
          </>
        )) || <></>
      }
    />
  );
}
